import { useContext, useEffect, useCallback } from 'react'

import { id } from 'hooks'

import { TabsContext, IRawTab } from './provider'

interface IUseTabsReturn {
  selectedTab: IRawTab
  rawTabs: IRawTab[]
  tabs: string[]
  handleAddTab: (_props: { tabName: string, createAndNavigateToNewTab?: boolean }) => void
  handleRemoveTab: (_props: { tabName?: string, removeAndNavigateToPreviousDefaultTab?: boolean, position?: number }) => void
  handleChangeTab: (_event: React.ChangeEvent | undefined, _tabPosition: number) => void
  handleChangeTabName: (newTabName: string, position?: number) => void
  tabNavigate: {
    goBack: () => void
  }
  controlTabs: {
    tabs: string[]
    value: number
    handleChange: (_event: React.ChangeEvent | undefined, _tabPosition: number) => void
  }
}

interface IUseTabsActions {
  onExitTab?: () => void
}

const useTabs = (defaultTabs: string[] = [], actions?: IUseTabsActions): IUseTabsReturn => {
  const {
    rawTabs,
    setRawTabs,
    selectedTab,
    setSelectedTab,
    lastSelectedDefaultTab,
    setLastSelectedDefaultTab,
  } = useContext(TabsContext)

  const getNamesOfTabs = useCallback((): string[] => {
    return rawTabs.map(tab => tab.name)
  }, [rawTabs])

  const getRawTabs = useCallback((): IRawTab[] => {
    return rawTabs.map((tab, position) => ({
      ...tab,
      position
    }))
  }, [rawTabs])

  const handleRemoveTab = (props: { tabName?: string, removeAndNavigateToPreviousDefaultTab?: boolean, position?: number }): void => {
    if (rawTabs.length) {
      if (props.tabName) {
        setRawTabs(prevState => prevState.filter(tab => !tab.name.includes(props.tabName)))
      }

      if (props.position) {
        setRawTabs(prevState => prevState.filter(tab => tab.position !== props.position))
      }

      if (props.removeAndNavigateToPreviousDefaultTab) {
        setSelectedTab(lastSelectedDefaultTab)
      }
    }
  }

  const handleAddTab = (props: { tabName: string, createAndNavigateToNewTab?: boolean }): void => {
    const newTab = {
      isDefault: false,
      name: props.tabName,
      position: rawTabs.length,
      tabID: id(),
    }

    setRawTabs(prevState => [...prevState, newTab])
    if (props.createAndNavigateToNewTab) {
      setSelectedTab(newTab)
    }
  }

  const handleChangeTab = (_event: React.ChangeEvent | undefined, tabPosition: number): void => {
    const selectedTab = getRawTabs().find(tab => tab.position === tabPosition)
    setSelectedTab(selectedTab)

    if (selectedTab.isDefault) {
      setLastSelectedDefaultTab(selectedTab)
    }
  }

  const handleChangeTabName = (newTabName: string, position?: number): void => {
    const newRawTabs = rawTabs.map((tab) => ({
      ...tab,
      name: tab.position === (position ? position : selectedTab.position) ? newTabName : tab.name
    }))
    setRawTabs(newRawTabs)
  }

  const goBack = (): void => {
    handleChangeTab(null, 0)
  }

  useEffect(() => {
    if (!rawTabs.length) {
      const raw_tabs = defaultTabs.map((tab, position) => ({
        isDefault: true,
        name: tab,
        position,
        tabID: id(),
      }))

      setSelectedTab(raw_tabs[0])
      setRawTabs(raw_tabs)
    }
  }, [])

  useEffect(() => () => actions?.onExitTab && actions.onExitTab(), [selectedTab])

  return {
    controlTabs: {
      handleChange: handleChangeTab,
      tabs: getNamesOfTabs(),
      value: selectedTab.position,
    },
    handleAddTab,
    handleChangeTab,
    handleChangeTabName,
    handleRemoveTab,
    rawTabs: getRawTabs(),
    selectedTab,
    tabNavigate: {
      goBack,
    },
    tabs: getNamesOfTabs(),
  }
}

export default useTabs
